import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Flex,
  Form,
  Icon,
  Icons,
  Input,
  Paragraph,
  Row,
  Switch,
  useToast,
} from "@ir/ui";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Permissions } from "@src/domain/permissions";
import { UpdatePurchaserDTO } from "@src/domain/dto/purchasers";
import type { Purchaser } from "@src/domain/entities/purchaser";
import { usePurchasersData } from "@src/presentation/providers/PurchasersDataProvider";
import { PurchaserConfigTemplateModal } from "./PurchaserConfigTemplateModal";
import { PurchaserPortalKeyModal } from "./PurchaserPortalKeyModal";
import { PurchaserStatusToggle } from "./PurchaserStatusToggle";

interface PurchaserAccordionTitleProps {
  purchaser: Purchaser;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
}

interface EditPurchaserFormValues {
  purchaserName: string;
  purchaserKey: string;
}

export const PurchaserAccordionTitle = ({
  purchaser,
  isEditing,
  onEdit,
  onCancelEdit,
}: PurchaserAccordionTitleProps) => {
  const { updatePurchaser, applyPurchaserUpdate } = usePurchasersData();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<EditPurchaserFormValues>();
  const [isActive, setIsActive] = useState(purchaser.purchaserIsActive);
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfigTemplateOpen, setIsConfigTemplateOpen] = useState(false);
  const [isPortalKeyOpen, setIsPortalKeyOpen] = useState(false);

  const purchaserNameRules = useMemo(
    () => [
      { required: true, message: "Purchaser name is required" },
      {
        max: 100,
        message: "Purchaser name must not exceed 100 characters",
      },
    ],
    [],
  );

  useEffect(() => {
    form.setFieldsValue({
      purchaserName: purchaser.purchaserName,
      purchaserKey: purchaser.purchaserKey,
    });
    setIsActive(purchaser.purchaserIsActive);
    setIsSubmittable(false);
  }, [form, purchaser]);

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);
    const values = form.getFieldsValue();
    const trimmedName = values.purchaserName?.trim() ?? "";
    const hasRequiredValues = trimmedName !== "";
    const hasChanges =
      trimmedName !== purchaser.purchaserName.trim() ||
      isActive !== purchaser.purchaserIsActive;
    setIsSubmittable(hasRequiredValues && !hasErrors && hasChanges);
  }, [form, isActive, purchaser.purchaserIsActive, purchaser.purchaserName]);

  useEffect(() => {
    if (isEditing) {
      updateFormValidity();
    }
  }, [isActive, isEditing, updateFormValidity]);

  const handleSave = async (values: EditPurchaserFormValues) => {
    setIsSaving(true);
    try {
      const validated = UpdatePurchaserDTO.validate({
        purchaserId: purchaser.purchaserId,
        purchaserName: values.purchaserName.trim(),
        isActive,
      });
      if (!validated.success) {
        throw new Error("Validation failed");
      }

      const updated = await updatePurchaser(
        new UpdatePurchaserDTO(validated.data),
      );
      applyPurchaserUpdate(updated);
      successToast({
        content: "Purchaser Updated successfully.",
        icon: <Icon name={Icons.CheckCircle} />,
      });
      onCancelEdit();
    } catch (error) {
      console.error("Failed to update purchaser", error);
      errorToast({
        content: "Failed to update. Please try again.",
        icon: <Icon name={Icons.AlertCircle} />,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Flex
        align="center"
        justifyContent="space-between"
        className="flex-1 min-w-0"
        style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
      >
        <Form<EditPurchaserFormValues>
          form={form}
          onFinish={handleSave}
          onFieldsChange={updateFormValidity}
          className="purchaser-form flex-1 min-w-0"
        >
          <Flex align="center" justifyContent="space-between" gap={1}>
            <Row style={{ flex: 1 }}>
              <Col span={6}>
                <Form.Item
                  name="purchaserName"
                  validateTrigger={["onChange", "onBlur"]}
                  rules={purchaserNameRules}
                >
                  <Input placeholder="Enter Purchaser Name" maxLength={100} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="purchaserKey">
                  <Input
                    placeholder="KEY - UPPERCASE-WITH-DASHES"
                    isDisabled
                    readOnly
                  />
                </Form.Item>
              </Col>
            </Row>
            <Flex align="center" gap={0.5} className="me-2">
              <Switch
                size="small"
                isSelected={isActive}
                onChange={setIsActive}
                isDisabled={isSaving}
              />
              <Button
                size="small"
                color="primary"
                variant="clear"
                onPress={() => form.submit()}
                isLoading={isSaving}
                isDisabled={isSaving || !isSubmittable}
              >
                <Icon name={Icons.Check} />
              </Button>
              <Button
                size="small"
                color="grey"
                variant="clear"
                onPress={onCancelEdit}
                isDisabled={isSaving}
              >
                <Icon name={Icons.Close} />
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    );
  }

  return (
    <>
      <Flex
        align="center"
        justifyContent="space-between"
        style={{ flex: 1, paddingTop: "0.25rem" }}
      >
        <Flex
          align="center"
          gap={0.5}
          className="flex-1 min-w-0 purchaser-title-info"
          style={{ paddingBottom: "0.25rem" }}
        >
          <Paragraph className="text-grey-700 font-bold m-0 text-lg">
            {purchaser.purchaserName}
          </Paragraph>

          <Flex
            align="center"
            gap={0.25}
            className="bg-white border-1 border-color-grey-25 rounded-small"
            style={{ padding: "2px 4px" }}
          >
            <Paragraph
              className="text-grey-500 m-0"
              style={{ lineHeight: "1", paddingLeft: "4px" }}
            >
              {purchaser.purchaserKey}
            </Paragraph>
            <Button
              size="small"
              variant="clear"
              color="grey"
              onPress={() => {
                navigator.clipboard.writeText(purchaser.purchaserKey);
                successToast({
                  content: "Purchaser Key copied to clipboard",
                  icon: <Icon name={Icons.CheckCircle} />,
                });
              }}
              style={{ padding: "2px" }}
            >
              <Icon name={Icons.Copy} />
            </Button>
          </Flex>
        </Flex>

        <Flex align="center" gap={0.5} className="me-2">
          <HasAnyPermission requiredPermissions={[Permissions.PURCHASERS_EDIT]}>
            <Button
              size="small"
              variant="outline"
              className="me-2"
              onPress={() => setIsPortalKeyOpen(true)}
            >
              Portal Key
            </Button>
          </HasAnyPermission>
          <HasAnyPermission requiredPermissions={[Permissions.PURCHASERS_EDIT]}>
            <Button
              size="small"
              variant="outline"
              className="me-2"
              onPress={() => setIsConfigTemplateOpen(true)}
            >
              Config Template
            </Button>
          </HasAnyPermission>
          <PurchaserStatusToggle purchaser={purchaser} />
          <HasAnyPermission requiredPermissions={[Permissions.PURCHASERS_EDIT]}>
            <Button size="small" color="grey" variant="clear" onPress={onEdit}>
              <Icon name={Icons.Edit} />
            </Button>
          </HasAnyPermission>
        </Flex>
      </Flex>

      <PurchaserPortalKeyModal
        purchaserId={isPortalKeyOpen ? purchaser.purchaserId : null}
        purchaserKey={purchaser.purchaserKey}
        purchaserName={purchaser.purchaserName}
        onClose={() => setIsPortalKeyOpen(false)}
      />
      <PurchaserConfigTemplateModal
        purchaserId={isConfigTemplateOpen ? purchaser.purchaserId : null}
        purchaserName={purchaser.purchaserName}
        onClose={() => setIsConfigTemplateOpen(false)}
      />
    </>
  );
};
